import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Stepper from "bs-stepper";
import "bs-stepper/dist/css/bs-stepper.min.css";
import "bootstrap/dist/css/bootstrap.min.css";
import SelectVehicle from "./SelectVehicle";
import SelectServicesPage from "./SelectServicesPage";
import SelectCustomerStep from "./SelectCustomerStep";
import { useLocation } from "react-router-dom";
import apiManager from '../../api/apiManager';
import API_ENDPOINTS from '../../api/apiEndpoints';
import SignedPage from "./SignedPage";
import InProgressPage from "./InProgressPage";
import ClosedPage from "./ClosedPage";
import CompletePage from "./CompletePage";
import { useVehicleContext } from '../../context/VehicleContext';
import ServiceOrderStatus from "../../shared/Enums/ServiceOrderStatus";
import { useSelectedDealership } from '../../hooks/useLocalStorage';
import { showErrorMessage } from '../../shared/Services/toasterService';
import { showLoader, hideLoader } from '../../shared/Services/Loader/loaderService';
import { useLocalization } from '../../context/LocalizationContext';

const ServiceOrderStepperForm = () => {
  const { t:localization } = useLocalization();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const ctx = useVehicleContext();
  const {
    status,
    setStatus,
    setSelectedServices,
    setRepairOrder,
    setCustomer,
    setVehicle,
    setCustomerId,
    setVehicleId,
    showModal,
    setShowModal,
    searchText,
    setSearchText,
    searchResults,
    setSearchResults,
    setShowTable,
    setDealershipId,
    stepperRef,
    stepper
  } = ctx;
 
  const location = useLocation();
  const [selectedDealershipId] = useSelectedDealership();
  const suppressStatusNavRef = useRef(false);

  useEffect(() => {
    stepper.current = new Stepper(stepperRef.current, {
      linear: false,
      animation: true,
    });
    stepperRef.current.addEventListener('shown.bs-stepper', (event) => {
      setCurrentStepIndex(event.detail.indexStep);
    });
    setTimeout(() => {
      if (stepper.current) stepper.current.to(1);
      setCurrentStepIndex(0);
    }, 100);

    const state = location.state || {};
    // If caller requested a fresh start, clear existing context so we don't resume a previous order
    if (state.reset) {
      // clear shared context
      setRepairOrder(null);
      setSelectedServices([]);
      setStatus(ServiceOrderStatus.NEW);
      setCustomer(null);
      setVehicle(null);
      setCustomerId(null);
      setVehicleId(null);
      setShowTable(false);
      setSearchResults([]);
      setSearchText("");
  // prevent the status effect from immediately moving the stepper to a different step
  suppressStatusNavRef.current = true;
  setTimeout(() => { suppressStatusNavRef.current = false; }, 800);
      // ensure the stepper shows the first step
      setTimeout(() => {
        if (stepper.current) stepper.current.to(1);
        setCurrentStepIndex(0);
      }, 120);
  // stop processing any other incoming navigation state when a reset was requested
  return;
    }

    // Check if this is a fresh page load (no meaningful state)
    const hasNavigationState = state.customer || state.vehicle || state.customerId || state.vehicleId ||
                              state.goToStep2 || state.goToStep3 || state.goToStep4 || state.goToStep5 ||
                              state.goToStep6 || state.goToStep7 || state.dealershipId || state.repairOrder ||
                              state.selectedServices || state.status || state.isViewMode || state.isEditMode;

    if (!hasNavigationState) {
      // This is a fresh page load - always go to first step regardless of status
      setRepairOrder(null);
      setSelectedServices([]);
      setStatus(ServiceOrderStatus.NEW);
      setCustomer(null);
      setVehicle(null);
      setCustomerId(null);
      setVehicleId(null);
      setShowTable(false);
      setSearchResults([]);
      setSearchText("");

      // Prevent status-based navigation for a short time
      suppressStatusNavRef.current = true;
      setTimeout(() => { suppressStatusNavRef.current = false; }, 800);

      // Ensure stepper stays on first step
      setTimeout(() => {
        if (stepper.current) stepper.current.to(1);
        setCurrentStepIndex(0);
      }, 120);
      return;
    }

    if (state.customer) setCustomer(state.customer);
    if (state.vehicle) setVehicle(state.vehicle);
    if (state.customerId) setCustomerId(state.customerId);
    if (state.vehicleId) setVehicleId(state.vehicleId);
    if (state.goToStep2) {
      setTimeout(() => {
        if (stepper.current) stepper.current.to(2);
        setCurrentStepIndex(1);
      }, 300);
    }
    if (state.goToStep3) {
      setTimeout(() => {
        if (stepper.current) stepper.current.to(3);
        setCurrentStepIndex(2);
      }, 500);
    }

    const {
      customer,
      vehicle,
      customerId: stateCustomerId,
      vehicleId: stateVehicleId,
      dealershipId: stateDealershipId,
      repairOrder,
      selectedServices: stateSelectedServices,
      status: stateStatus,
      isViewMode: stateIsViewMode,
      isEditMode: stateIsEditMode
    } = location.state || {};

    if (stateDealershipId) {
      setDealershipId(stateDealershipId);
    }

    if (stateSelectedServices && Array.isArray(stateSelectedServices)) {
      setSelectedServices(stateSelectedServices);
    }

    // Set status from navigation state
    if (stateStatus) {
      setStatus(stateStatus);
    }

    // Handle repair order data and status
    if (repairOrder) {
      setRepairOrder(repairOrder);

      // Set customer and vehicle data from repair order if not already set
      if (!customer && repairOrder.customerName) {
        setCustomer({
          name: repairOrder.customerName,
          id: repairOrder.customerId
        });
      }

      // Normalize and set status to match ServiceOrderStatus enum values
      if (repairOrder.statusCode) {
        let normalizedStatus = repairOrder.statusCode.toLowerCase();
        if (normalizedStatus === ServiceOrderStatus.IN_PROGRESS || normalizedStatus === "in progress") {
          normalizedStatus = ServiceOrderStatus.IN_PROGRESS; // 'inprogress'
        } else if (normalizedStatus === ServiceOrderStatus.SIGNED) {
          normalizedStatus = ServiceOrderStatus.SIGNED; // 'signed'
        } else if (normalizedStatus === ServiceOrderStatus.COMPLETED) {
          normalizedStatus = ServiceOrderStatus.COMPLETED; // 'completed'
        } else if (normalizedStatus === ServiceOrderStatus.CLOSED) {
          normalizedStatus = ServiceOrderStatus.CLOSED; // 'closed'
        } else {
          normalizedStatus = ServiceOrderStatus.NEW; // 'new'
        }
        setStatus(normalizedStatus);
      }
    } else if (location.state?.status) {
      setStatus(location.state.status);
    }

    // Navigate to appropriate step based on location state
    if (location.state?.goToStep2) {
      setTimeout(() => {
        if (stepper.current) stepper.current.to(2);
        setCurrentStepIndex(1);
      }, 300);
    }
    if (location.state?.goToStep3) {
      setTimeout(() => {
        if (stepper.current) stepper.current.to(3);
        setCurrentStepIndex(2);
      }, 500);
    }
    if (location.state?.goToStep4) {
      setTimeout(() => {
        if (stepper.current) stepper.current.to(4);
        setCurrentStepIndex(3);
      }, 700);
    }
    if (location.state?.goToStep5) {
      setTimeout(() => {
        if (stepper.current) stepper.current.to(5);
        setCurrentStepIndex(4);
      }, 900);
    }
    if (location.state?.goToStep6) {
      setTimeout(() => {
        if (stepper.current) stepper.current.to(6);
        setCurrentStepIndex(5);
      }, 1100);
    }
    if (location.state?.goToStep7) {
      setTimeout(() => {
        if (stepper.current) stepper.current.to(7);
        setCurrentStepIndex(6);
      }, 1300);
    }
  }, [location.state]);

  useEffect(() => {
    if (!stepper.current) return;
    if (suppressStatusNavRef.current) return; // do not auto-navigate when a manual reset just happened
    if (status === ServiceOrderStatus.SIGNED) {
      stepper.current.to(4);
      setCurrentStepIndex(3);
    } else if (status === ServiceOrderStatus.IN_PROGRESS) {
      stepper.current.to(5);
      setCurrentStepIndex(4);
    } else if (status === ServiceOrderStatus.COMPLETED) {
      stepper.current.to(6);
      setCurrentStepIndex(5);
    }
    else if (status === ServiceOrderStatus.CLOSED) {
      stepper.current.to(7);
      setCurrentStepIndex(6);
    }
  }, [status]);

  const closeModal = () => setShowModal(false);
  const steps = [
    { id: "step1", label: localization("step_select_customer") },
    { id: "step2", label: localization("step_select_vehicle") },
    { id: "step3", label: localization("step_select_services") },
    { id: "step4", label: localization("step_signed") },
    { id: "step5", label: localization("step_in_progress") },
    { id: "step6", label: localization("step_complete") },
    { id: "step7", label: localization("step_closed") },
  ];

  const stepComponents = {
  step1: (<SelectCustomerStep />),
  step2: (<SelectVehicle />),
  step3: (<SelectServicesPage />),
  step4: (<SignedPage />),
  step5: (<InProgressPage />),
  step6: (<CompletePage />),
  step7: (<ClosedPage />)
  };

  return (
    <div className="container-fluid content content-wrapper">
      <div id="stepper" ref={stepperRef} className="bs-stepper">
        <div
          className="bs-stepper-header"
          role="tablist"
          style={{ overflowX: "auto", whiteSpace: "nowrap" }}
        >
          <style>{`
            .step-completed .bs-stepper-circle {
              background-color: #28a745 !important;
              color: white;
              border-color: #28a745;
            }
            .step-current .bs-stepper-circle {
              background-color: #fff !important;
              color: #007bff;
              border: 2.5px solid #007bff;
              font-size: 1.35em;
              box-shadow: 0 0 8px #007bff33;
              z-index: 2;
              transition: all 0.2s;
            }
            .step-current .bs-stepper-label {
              font-weight: bold;
              color: #007bff;
              font-size: 1.07em;
              letter-spacing: 0.5px;
            }
          `}</style>
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className={`step${index < currentStepIndex ? " step-completed" : ""}${index === currentStepIndex ? " step-current" : ""}`} data-target={`#${step.id}`}>
                <button
                  type="button"
                  className="step-trigger"
                  role="tab"
                  aria-controls={step.id}
                  id={`stepper-trigger-${index + 1}`}
                  disabled
                  style={{ pointerEvents: 'none', cursor: 'default', background: 'none', border: 'none' }}
                >
                  <span className="bs-stepper-circle">{index + 1}</span>
                  <span className="bs-stepper-label">{step.label}</span>
                </button>
              </div>
              {index < steps.length - 1 && <div className="line" />}
            </React.Fragment>
          ))}
        </div>
        <div className="bs-stepper-content">
          {steps.map((step, index) => (
            <div
              key={step.id}
              id={step.id}
              className="content"
              role="tabpanel"
              aria-labelledby={`stepper-trigger-${index + 1}`}
            >
              {index === currentStepIndex ? (
                stepComponents[step.id]
              ) : null}
              <div className="d-flex gap-2 mt-4">
              </div>
            </div>
          ))}
        </div>
      </div>
      {showModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title text-black">{localization("customer_details")}</h5>
                  <button type="button" className="btn-close btn-close-white" onClick={closeModal}></button>
                </div>
                <div className="modal-body">
                  <div className="table-responsive">
                    <table className="table table-bordered table-hover table-sm">
                      <thead className="thead-light">
                        <tr>
                          <th>{localization("customer")}</th>
                          <th>{localization("address")}</th>
                          <th>{localization("vehicle")}</th>
                          <th>{localization("vin")}</th>
                          <th>{localization("action")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.customer}</td>
                            <td>{item.address}</td>
                            <td>{item.vehicle}</td>
                            <td>{item.vin}</td>
                            <td>
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => {
                                  closeModal();
                                  stepper.current.to(2);
                                }}
                              >
                                <i className="fas fa-check me-1"></i> {localization("select")}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={closeModal}>
                    <i className="fas fa-times me-1"></i> {localization("close")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ServiceOrderStepperForm;